import {api} from "../api";
import {FancytreeContextMenu} from "./fancytreeContextMenu";
import {promiseFailNow, promiseNow} from "../utils/promise";
import {mergeProperties} from "@/shared/utils/misc";
import FancytreeNode = Fancytree.FancytreeNode;
import FancytreeOptions = Fancytree.FancytreeOptions;

export interface BaseTreeConfig {
	treeElement: JQuery;
	loadRootNodesURL: string;
	loadSubNodesURL: string;
	filterNodesURL: string;
	getRequestData: () => Record<string, unknown>;
	isNodeSelectable: (data: unknown) => boolean;
	minQueryLength?: number;
	contextMenu?: FancytreeContextMenu<unknown>;
	deferLoading?: boolean;
	onRenderNode?: (event: JQueryEventObject, data: Fancytree.EventData) => void;
	onEnhanceTitle?: (event: JQueryEventObject, data: Fancytree.EventData) => void;
	dndSettings?: Fancytree.Extensions.DragAndDrop;
	pagingSize?: number;
	emptyTreeMessage?: string;
	noMatchMessage?: string;
	clickFolderMode?: number;
}

export interface BaseTreeBasicNode {
	title: string;
	children: [];
	unselectable: boolean;
	emptyNode: boolean;
	dummy: boolean;
	clearEntryNode?: boolean;
}

/**
 * Control fields stored on `node.data` for status/paging nodes. `@types/jquery.fancytree`
 * types `node.data` as `unknown`, so these reads are narrowed via this shape.
 */
export interface BaseTreeNodeData {
	dummy?: boolean;
	emptyNode?: boolean;
	rootNode?: boolean;
	nextStart?: number;
}

export class BaseTree {

	public static EVENT_SELECTED = "selected";
	public static EVENT_TREE_INITIALIZED = "treeInitialized";

	private tree: Fancytree.Fancytree;
	private query: string;
	private filterWithEmptyQuery: boolean;
	private disableLoading = false;
	private initialized = false;

	constructor(public config: BaseTreeConfig) {
		if (config.deferLoading) {
			this.disableLoading = true;
		}

		this.initTree();
	}

	public queryTree(query: string, ignoreEmptyQuery = false) {
		this.query = query;
		this.filterWithEmptyQuery = !query && ignoreEmptyQuery;
		this.reload();
	}

	public reload() {
		if (this.initialized) {
			return this.tree.reload();
		} else {
			return promiseFailNow("Not initialized");
		}
	}

	public getActiveNode(): FancytreeNode {
		return this.tree.getActiveNode();
	}

	public getNode(element: JQuery): FancytreeNode {
		return $.ui.fancytree.getNode(element);
	}

	public getNodeByKey(key: string): FancytreeNode {
		return this.tree.getNodeByKey(key);
	}

	public getRoot(): FancytreeNode {
		return this.tree.getRootNode();
	}

	public setDisableLoading(disableLoading: boolean) {
		this.disableLoading = disableLoading;
		return this.reload();
	}

	public setMaxHeight(height: number) {
		this.config.treeElement.children("ul.fancytree-container")
			.css("max-height", `${height}px`);
	}

	public removeDummyNodes() {
		const rootChildren = this.getRoot().getChildren();
		if (rootChildren) {
			rootChildren.filter(node => (node.data as BaseTreeNodeData)?.dummy)
				.forEach(node => node.remove());
		}
	}

	private addEmptyNode() {
		const root = this.getRoot();
		if (root) {
			const emptyNode: BaseTreeBasicNode = {
				dummy: true,
				emptyNode: true,
				title: messages["tree.empty"],
				children: [],
				unselectable: true
			};
			root.addNode(emptyNode);
		}
	}

	protected getTreeSourcePromise() {
		if (this.disableLoading) {
			return promiseNow([{
				emptyNode: true,
				dummy: true,
				title: messages["tree.empty"],
				children: [],
				unselectable: true
			}]);
		}

		if (this.isQueryTooShort()) {
			return promiseNow([{
				dummy: true,
				title: messages.withArguments("search.query.minlength", this.config.minQueryLength),
				children: [],
				unselectable: true
			}]);
		}

		const url = this.query || this.filterWithEmptyQuery ? this.config.filterNodesURL : this.config.loadRootNodesURL;
		let requestData = this.config.getRequestData();
		if (this.query) {
			requestData = mergeProperties(requestData, {query: this.query});
		}

		if (this.config.pagingSize) {
			requestData = mergeProperties(requestData, {
				count: this.config.pagingSize
			});
		}

		return api(url)
			.get(requestData)
			.then((data: Fancytree.NodeData[]) => {
				if (data.length === 0) {
					if (!requestData.query) {
						return [{
							emptyNode: true,
							dummy: true,
							title: messages["tree.empty"],
							children: [] as Fancytree.NodeData[],
							unselectable: true
						}];
					} else {
						return [{
							dummy: true,
							title: messages["search.query.nomatch"],
							children: [] as Fancytree.NodeData[],
							unselectable: true
						}];
					}
				} else {
					return data;
				}
			});
	}

	private initTree() {
		const extensions = ["glyph", "treeassign"];

		if (this.config.dndSettings) {
			extensions.push("dnd");
		}

		const treeConfig: FancytreeOptions = {
			extensions: extensions,
			glyph: {
				preset: "awesome4",
				map: {
					expanderOpen: "far fa-chevron-down",
					expanderClosed: "far fa-chevron-right",
					expanderLazy: "far fa-chevron-right",
					loading: "far fa-spinner fa-spin"
				}
			},
			escapeTitles: true,
			tooltip: false,
			selectMode: 1,
			clickFolderMode: this.config.clickFolderMode ?? 4,	// activate = 1, expand = 2, activate_and_expand = 3, activate_dblclick_expands = 4 (default)
			source: () => this.getTreeSourcePromise(),
			lazyLoad: (event: JQueryEventObject, data: Fancytree.EventData) => {
				const node = data.node;

				let requestData = mergeProperties(this.config.getRequestData(), {selectedId: node.key});

				if (this.config.pagingSize) {
					requestData = mergeProperties(requestData, {
						count: this.config.pagingSize
					});
				}

				data.result = {
					url: "/timr/" + this.config.loadSubNodesURL,
					data: requestData,
					cache: false
				};
			},
			enhanceTitle: (e: JQueryEventObject, data: Fancytree.EventData) => {
				const node = data.node;
				if (!this.config.isNodeSelectable(node.data)) {
					node.addClass("not-selectable");
				}
				if ((node.data as BaseTreeNodeData)?.dummy === true) {
					node.addClass("node-dummy");
				}
				if (this.config.onEnhanceTitle) {
					this.config.onEnhanceTitle(e, data);
				}
			},
			icon: (e: JQueryEventObject, data: Fancytree.EventData) => {
				// The glyph extension does not add a loading icon to the loading statusnode
				// but only on lazy node loading so it has to be added manually here.
				if (data.node.statusNodeType === "loading") {
					return data.options.glyph?.map?.loading;
				}
				return false;
			},
			click: (e: JQueryEventObject, data: Fancytree.EventData) => {
				/* DEVNOTE: For a tree which allows selections 'outside' the tree it's necessary that the node selected event is triggered even
				 * The node is already selected. "Click" differs from "Activate" in that it is triggered again if the node is already active.
				 * For the event used in components TreeDropdown.EVENT_SELECTED, check for the presence of a change in the selection event is already implemented,
				 * This got changed by TIMR-8665
				 */
				const node = data.node, targetType = data.targetType;

				// Trigger node selected event when a selectable node is clicked (restriction 'title' prevents triggering when clicking on expander)
				if (targetType === "title") {
					if (this.config.isNodeSelectable(node.data)) {
						node.setActive(true);
						this.onNodeSelected(node);
					} else {
						node.setActive(false);
						node.toggleExpanded();
					}
				}
				return true;
			},
			beforeActivate: (e: JQueryEventObject, data: Fancytree.EventData) => {
				const node = data.node;
				if ((node.data as BaseTreeNodeData).dummy === true) {
					$(node.span).closest(".dropdown-menu").hide();
					return false;
				}
				// to avoid a changing selection it is necessary that a click on a non-selectable node does not changes the active node, see TIMR-8665
				if (!this.config.isNodeSelectable(node.data)) {
					return false;
				}
			},
			modifyChild: (e: JQueryEventObject, data: Fancytree.EventData) => {
				if (data.operation === "remove" && this.tree.count() === 1) {
					const node = this.getRoot().getChildren()[0];
					const nodeData = node.data as BaseTreeNodeData;
					if (!(node.statusNodeType === "loading" || nodeData && nodeData.dummy && nodeData.emptyNode)) {
						this.addEmptyNode();
					}
				}
			},
			beforeExpand: (e: JQueryEventObject, data: Fancytree.EventData) => {
				if (!e.originalEvent) {
					return false;
				} else {
					return $(e.originalEvent.target).hasClass("fancytree-expander") ||
						$(e.originalEvent.target).hasClass("fancytree-title") && !this.config.isNodeSelectable(data.node.data);
				}
			},
			renderTitle: (event: JQueryEventObject, data: Fancytree.EventData) => {
				const node = data.node;
				const nodeData = node.data as BaseTreeNodeData;
				if (this.isQueryTooShort()) {
					return;
				}
				if (nodeData?.dummy) {
					/*
					 * A (customized) emptyTreeMessage or noMatchMessage will override the default ones.
					 * The reason of this is the default title escaping of the library (escapeTitles).
					 *
					 * It would be possible to do this directly at the data fetch getTreeSourcePromise(),
					 * but then we would have to take care of the escaping ourselves, which could lead to risks (in the future)
					 */
					if (this.config.emptyTreeMessage && nodeData.emptyNode) {
						return this.config.emptyTreeMessage;
					} else if (this.config.noMatchMessage && !nodeData.emptyNode) {
						return this.config.noMatchMessage;
					}
				}
			},
			renderNode: (event: JQueryEventObject, data: Fancytree.EventData) => {
				const node = data.node;
				const nodeSpan = $(node.span);
				const nodeData = node.data as BaseTreeNodeData;
				if (!nodeSpan.data("rendered-menu") && !nodeData?.dummy && !node.statusNodeType) {
					if (this.config.contextMenu) {
						nodeSpan.data("node-key", node.key)
							.append(this.config.contextMenu.render(node.data))
							.data("rendered-menu", true);
					}
				}
				if (this.config.onRenderNode) {
					this.config.onRenderNode(event, data);
				}
			},
			init: (event: JQueryEventObject, data: Fancytree.EventData) => {
				this.initialized = true;
				$(this).triggerHandler(BaseTree.EVENT_TREE_INITIALIZED, [data]);
			},
			clickPaging: (event: JQueryEventObject, data: Fancytree.EventData) => {
				const parent = data.node.parent;
				const nodeData = data.node.data as BaseTreeNodeData;

				const path = nodeData?.rootNode
					? (this.query || this.filterWithEmptyQuery ? this.config.filterNodesURL : this.config.loadRootNodesURL)
					: this.config.loadSubNodesURL;

				const requestData = mergeProperties(this.config.getRequestData(), {
					query: this.filterWithEmptyQuery ? null : this.query,
					selectedId: parent.key
				}, {
					// The nextStart data field of the paging node is provided by the backend.
					// This is used as the offset for the next request
					start: nodeData?.nextStart || 0,
					count: this.config.pagingSize
				});

				const url = api(path).url + "?" + $.param(requestData);

				// Replace the paging node with the nodes of the next page
				data.node.replaceWith({url});
				return false;
			},
			dnd: this.config.dndSettings
		};

		this.config.treeElement.fancytree(treeConfig);

		this.tree = $.ui.fancytree.getTree(this.config.treeElement);

		if (this.config.contextMenu) {
			this.config.contextMenu.registerOnFancytree(this.tree);
		}
	}

	/**
	 * @returns {boolean} true, if the query is a string and the minQueryLength is set and the query is <b>shorter</b> than the minQueryLength
	 * @private
	 */
	private isQueryTooShort(): boolean {
		if (this.filterWithEmptyQuery) {
			return false;
		}
		return this.query && this.config.minQueryLength && this.query.length < this.config.minQueryLength;
	}

	private onNodeSelected(node: FancytreeNode) {
		$(this).triggerHandler(BaseTree.EVENT_SELECTED, [node.data, node]);
	}
}
